import React, {
  useMemo,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from 'react'
import * as Yup from 'yup'
import { isEqual, isEmpty } from 'lodash'
import { useFormik } from 'formik'
import PropTypes from 'prop-types'
import ReactTooltip from 'react-tooltip'
import { useLocation } from 'react-router-dom'

import Box from '@material-ui/core/Box'
import Grid from '@material-ui/core/Grid'
import Input from '@material-ui/core/Input'
import FormControl from '@material-ui/core/FormControl'
import FormHelperText from '@material-ui/core/FormHelperText'

import makeStyles from '@material-ui/core/styles/makeStyles'

import { usePrice } from '../../../../../shared/contexts/PriceContext'
import useYup from '../../../../../shared/hooks/useYup'

import useUserClient from '../../../../../clients/UserClient/useUserClient'
import useReceitaWSClient from '../../../../../clients/ReceitaWSClient/useReceitaWSClient'
import useInsuredGuaranteeBidClient from '../../../../../clients/InsuredClient/useInsuredGuaranteeBidClient'

import {
  ButtonLight,
  ButtonGradient,
  CPFOrCNPJInput,
  useLoader, useAlert,
  CNPJ_SIZE_WITHOUT_MASK,
} from '../../../../../shared/components'
import { GlobalMessages } from '../../../../../shared/messages'
import CollapsePrice from '../../../../../shared/components/CollapsePrice/CollapsePrice'
import DropdownSelectOne from '../../../../../shared/components/DropdownSelectOne/DropdownSelectOne'

const useStyles = makeStyles((theme) => ({
  buttonsStep: {
    display: 'flex',
    justifyContent: 'flex-end',
    '& .MuiBox-root': {
      minWidth: 190,
      [theme.breakpoints.down('xs')]: {
        marginTop: 5,
        marginLeft: 5,
        minWidth: 115,
      },
    },
  },
  label: {
    color: theme.palette.text.quaternary,
    fontWeight: 'bold',
  },
  help: {
    color: theme.palette.text.quaternary,
  },
}))

const Insured = forwardRef((props, ref) => {
  const classes = useStyles()
  const userClient = useUserClient()
  const { state } = useLocation()

  const {
    quote,
    checked,
    handleStep,
    handleNext,
    handlePrevious,
  } = props
  const { cpfOrCnpj } = useYup()
  const { showLoader, hideLoader } = useLoader()
  const { addMsgDanger } = useAlert()
  const insuredGuaranteeBidClient = useInsuredGuaranteeBidClient()
  const receitaWSClient = useReceitaWSClient()

  const {
    actionNext,
    setFormikChanged,
    guaranteeBID,
    setGuaranteeBID,
    onNext,
  } = usePrice()

  const [claimantDisabled, setClaimantDisabled] = useState(true)

  const tomadorName = state?.tomadorName || quote?.Insured?.PolicyHolder || ''
  const tomadorDocument = state?.tomadorDocument || quote?.Insured?.DocumentNumber || ''

  const initialValuesForm = useMemo(() => ({
    name: quote?.Insured?.Name ?? '',
    document: quote?.Insured?.Document ?? '',
    BIDType: quote?.Insured?.BIDType ?? '',
    policyHolder: tomadorName,
    documentNumber: tomadorDocument,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [quote?.Insured, tomadorName, tomadorDocument])

  const policyHolderCnpj = useMemo(() => (
    tomadorDocument?.replace(/[.\-/\s]/g, '') ?? ''
  ), [tomadorDocument])

  const fieldValueIquals = () => (cpfOrCnpj
    .test('document', GlobalMessages.EQUAL_TO_POLICY_HOLDER,
      (value) => (
        value && value.replace(/[.\-/\s]/g, '') !== policyHolderCnpj
      ))
    .required())

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: initialValuesForm,
    validationSchema: Yup.object({
      document: fieldValueIquals(),
      name: !claimantDisabled ? Yup.string()
        .matches(/^[A-Za-z\s]+$/, 'Apenas letras são permitidas').required('Campo obrigatório') : Yup.string().required(),
      BIDType: Yup.string().required(),
    }),
    onSubmit: (data) => {
      const values = {
        ...guaranteeBID,
        ...{
          insured: { ...data },
        },
      }

      const {
        name,
        document,
      } = values.insured

      const valueInsuredObject = {
        name,
        document,
      }

      userClient().getClientByUserLogged().then((response) => {
        const filterNotLead = response?.data?.filter((item) => item?.lead === false)
        const filterNomination = filterNotLead
          ?.filter((item) => item?.cpfcnpj === values.insured.documentNumber)
        if (filterNomination?.length > 0) {
          setGuaranteeBID({ ...values, ClientIsNominated: true })
        } else {
          setGuaranteeBID({ ...values, ClientIsNominated: false })
        }
      }, () => {
        setGuaranteeBID({ ...values })
      })

      if (actionNext) {
        setFormikChanged(!isEqual(initialValuesForm, data))
        onNext(values)
        if (valueInsuredObject.document?.replace(/[.\-/\s]/g, '')?.length !== CNPJ_SIZE_WITHOUT_MASK) {
          userClient().createUpdateInsuredObjectProperty(valueInsuredObject)
            .then((response) => {
              addMsgDanger(response.data.error)
            })
        }
      }
    },
  })

  useImperativeHandle(ref, () => ({
    onSubmit: () => new Promise((resolve) => {
      formik.validateForm().then((errors) => {
        let data

        if (!isEmpty(errors)) {
          data = { message: GlobalMessages.VERIFIQUE_CAMPOS_DESTAQUE_ANTES_PROSSEGUIR }
        }

        resolve(data)
        formik.submitForm()
      })
    }),
  }))

  const [typesInsured, setTypesInsured] = useState([''])

  useEffect(() => {
    setTypesInsured(insuredGuaranteeBidClient().getTypes().sort())
  }, [insuredGuaranteeBidClient])

  const handleInfoCnpj = (evento) => {
    const { value } = evento.target

    const normalized = value?.replace(/[.\-/\s]/g, '') ?? ''

    if (normalized.length === CNPJ_SIZE_WITHOUT_MASK) {
      showLoader()
      receitaWSClient().getInfoByCnpj(normalized).then(async (response) => {
        if (response?.data?.nome) {
          formik.setFieldValue('name', response.data.nome)
          setClaimantDisabled(true)
        } else {
          setClaimantDisabled(false)
        }
        hideLoader()
      }).catch(() => {
        setClaimantDisabled(false)
        hideLoader()
      })
    } else {
      showLoader()
      setClaimantDisabled(false)
      userClient().getClientInsuredObjectProperty(normalized).then(async (response) => {
        if (response) {
          formik.setFieldValue('name', response?.data?.nome ?? '')
          hideLoader()
        }
      }).catch(() => {
        hideLoader()
      })
    }
  }

  const placeholderCPFCNPJ = '000.000.000-00 / 00.000.000/0000-00'

  const nameLabel = 'Segurado'
  const policyHolderLabel = 'Tomador'
  const cpfCnpjLabel = 'CPF/CNPJ do Segurado'
  const typeLabel = 'Tipo'

  return (
    <>
      <CollapsePrice
        checked={checked}
        handleEdit={handleStep}
        hiddenEdit={isEmpty(quote?.Insured) && !formik.isSubmitting}
        title="Dados do Segurado"
        subtitle="Informe abaixo os dados do segurado"
      >
        <Grid container spacing={5}>
          <Grid item sm={12} xs={12}>
            <FormControl fullWidth>
              <label htmlFor="policyHolderLabel" className={classes.label}>{policyHolderLabel}</label>
              <Input
                fullWidth
                id="policyHolder"
                color="secondary"
                disabled
                {...formik.getFieldProps('policyHolder')}
              />
              <FormHelperText>
                Caso queira alterar o tomador, retorne à etapa de Dados do Produto
              </FormHelperText>
            </FormControl>
          </Grid>
          <Grid item sm={6} xs={12}>
            <FormControl fullWidth>
              <label htmlFor="cpfCnpjLabel" className={classes.label}>{cpfCnpjLabel}</label>
              <CPFOrCNPJInput
                fullWidth
                id="document"
                color="secondary"
                placeholder={placeholderCPFCNPJ}
                {...formik.getFieldProps('document')}
                error={formik.touched.document && !!formik.errors.document}
                onBlur={handleInfoCnpj}
              />
              <FormHelperText
                hidden={!formik.touched.document || !formik.errors.document}
                error={formik.touched.document && !!formik.errors.document}
              >
                {formik.errors.document}
              </FormHelperText>
            </FormControl>
          </Grid>
          <Grid item sm={6} xs={12}>
            <FormControl fullWidth>
              <label htmlFor="nameLabel" className={classes.label}>{nameLabel}</label>
              <Input
                fullWidth
                id="name"
                color="secondary"
                placeholder="Nome do Segurado"
                disabled={claimantDisabled}
                {...formik.getFieldProps('name')}
                error={formik.touched.name && !!formik.errors.name}
              />
              <FormHelperText
                hidden={!formik.touched.name || !formik.errors.name}
                error={formik.touched.name && !!formik.errors.name}
              >
                {formik.errors.name}
              </FormHelperText>
            </FormControl>
          </Grid>
          <Grid item sm={6} xs={12}>
            <FormControl fullWidth>
              <label htmlFor="typeLabel" className={classes.label}>{typeLabel}</label>
              <DropdownSelectOne
                id="BIDType"
                color="secondary"
                options={typesInsured}
                {...formik.getFieldProps('BIDType')}
                error={formik.touched.BIDType && !!formik.errors.BIDType}
              />
              <FormHelperText
                hidden={!formik.touched.BIDType || !formik.errors.BIDType}
                error={formik.touched.BIDType && !!formik.errors.BIDType}
              >
                {formik.errors.BIDType}
              </FormHelperText>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Box className={classes.buttonsStep}>
              <Box>
                <ButtonLight
                  onClick={handlePrevious}
                  title="Voltar"
                >
                  Voltar
                </ButtonLight>
              </Box>
              <Box ml={4}>
                <ButtonGradient
                  onClick={handleNext}
                  title="Continuar"
                >
                  Continuar
                </ButtonGradient>
              </Box>
            </Box>
            <ReactTooltip place="top" type="dark" />
          </Grid>
        </Grid>
      </CollapsePrice>
    </>
  )
})

Insured.propTypes = {
  quote: PropTypes.any,
  checked: PropTypes.bool,
  handleStep: PropTypes.func.isRequired,
  handleNext: PropTypes.func.isRequired,
  handlePrevious: PropTypes.func.isRequired,
}

Insured.defaultProps = {
  checked: false,
  quote: undefined,
}

export default Insured
